import type { ParsedToken } from "firebase/auth";
import moment from "moment";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  track,
  usePostHubspotEvent,
  usePostHubspotProperty,
} from "@/features/analytics";
import { getCustomClaims } from "@/features/authentication";
import {
  WEBSITE_DIRECTORY_COOKIE_KEY,
  WEBSITE_TEMPLATE_COOKIE_KEY,
  WIZARD_COOKIE_KEY,
  WIZARD_DOCPILOT_COOKIE_KEY,
} from "@/features/wizard/constants";
import {
  type TUser,
  useGetCurrentUserQuery,
  useUpdateUserFirstLoginMutation,
} from "@/shared/services";
import {
  type TAccount,
  useGetAccountQuery,
  useSetFirstTimeLoginMutation,
} from "@/shared/services/account";
import { getCookie } from "@/shared/utils/cookies";
import { FUNNEL_PATHS } from "@/pages/how-to-start/funnel-paths";

/**
 * Cookie keys used for wizard navigation
 */
const WIZARD_COOKIES = {
  WIZARD: WIZARD_COOKIE_KEY,
  START_FROM_TEMPLATE: WEBSITE_TEMPLATE_COOKIE_KEY,
  START_FROM_SLIDE: WEBSITE_DIRECTORY_COOKIE_KEY,
  WIZARD_DOCPILOT: WIZARD_DOCPILOT_COOKIE_KEY,
} as const;

/**
 * Checks for wizard cookies and navigates if found
 */
const checkWizardPayloadAndNavigate = (
  navigate: (path: string) => void,
  claims: ParsedToken,
) => {
  const wizardData =
    getCookie(WIZARD_COOKIES.WIZARD) ||
    getCookie(WIZARD_COOKIES.START_FROM_TEMPLATE) ||
    getCookie(WIZARD_COOKIES.START_FROM_SLIDE) ||
    getCookie(WIZARD_COOKIES.WIZARD_DOCPILOT);

  const userType = claims?.type || "";

  if (!["user", "creator lite"].includes(userType as string) && wizardData) {
    navigate(FUNNEL_PATHS.root);
  }
};

/**
 * Extracts company name from wizard cookie
 */
const getCompanyFromWizardCookie = (): string => {
  const data = getCookie(WIZARD_COOKIES.WIZARD);
  if (!data) return "";

  try {
    const parsed = JSON.parse(data);
    return parsed["company name"] || "";
  } catch (e) {
    console.error("Error parsing wizard cookie:", e);
    return "";
  }
};

/**
 * Hook to handle first-time login logic and wizard navigation
 * Migrated from Angular FirstTimeLoginComponent
 */
export const useFirstTimeLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [processed, setProcessed] = useState(false);

  // Fetch account and user data
  const { data: account, isLoading: accountLoading } = useGetAccountQuery();
  const { data: user, isLoading: userLoading } = useGetCurrentUserQuery();

  const { mutate: postHubspotEvent } = usePostHubspotEvent();
  const { mutate: postHubspotProperty } = usePostHubspotProperty();

  const { mutate: setFirstTimeLogin } = useSetFirstTimeLoginMutation();
  const { mutate: updateUserFirstLogin } = useUpdateUserFirstLoginMutation();

  // Check for create query parameter
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has("create")) {
      navigate(FUNNEL_PATHS.root);
    }
  }, [location.search, navigate]);

  /**
   * Extracts UTM data from account parameters
   */
  const extractUtmData = (
    parameters: Record<string, unknown>,
  ): Record<string, unknown> => {
    const utmData: Record<string, unknown> = {};
    const utmKeys = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "gclid",
    ];

    for (const key of utmKeys) {
      utmData[key] = parameters[key] || "";
    }

    if (parameters.landingPage) {
      utmData.landing_page = parameters.landingPage;
    }
    if (parameters.landingPageCategory) {
      utmData.landing_page_category = parameters.landingPageCategory;
    }
    if (parameters.adwords_timestamp) {
      try {
        const date = moment
          .utc(parameters.adwords_timestamp as string)
          .startOf("day")
          .toDate();
        utmData.first_time_clicked_ad = date;
      } catch (e) {
        console.error("Error parsing adwords_timestamp:", e);
      }
    }

    return utmData;
  };

  /**
   * Gets formatted timezone information
   */
  const getTimezoneInfo = () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offsetMinutes = moment().utcOffset();
    const offsetSign = offsetMinutes <= 0 ? "+" : "-";
    const absOffsetMinutes = Math.abs(offsetMinutes);
    const offsetHours = Math.floor(absOffsetMinutes / 60);
    const offsetMins = absOffsetMinutes % 60;
    const formattedOffset = `${offsetSign}${offsetHours
      .toString()
      .padStart(2, "0")}:${offsetMins.toString().padStart(2, "0")}`;

    const timeZoneAbbreviation =
      new Intl.DateTimeFormat("en", {
        timeZoneName: "short",
        timeZone: timezone,
      })
        .formatToParts(new Date())
        .find((part) => part.type === "timeZoneName")?.value || "";

    return { formattedOffset, timeZoneAbbreviation };
  };

  /**
   * Sends analytics events for first login
   */
  const handleLoginAnalyticsEvents = async (account: TAccount) => {
    try {
      const eventDate = moment.utc().startOf("day").toDate();
      postHubspotEvent({ event: "view platform for the first time" });

      const parameters = account.parameters || {};
      const utmData = extractUtmData(parameters);
      const { formattedOffset, timeZoneAbbreviation } = getTimezoneInfo();

      postHubspotProperty({
        trial_status: "active",
        company_name: getCompanyFromWizardCookie(),
        view_platform_for_the_first_time: eventDate,
        last_editor_visit_date: eventDate,
        time_zone_abbreviation: timeZoneAbbreviation,
        time_zone_offset: formattedOffset,
        ...utmData,
      });
    } catch (error) {
      console.error("Error sending analytics events:", error);
    }
  };

  /**
   * Handles the actual first login flow
   */
  const handleFirstLoginFlow = async (account: TAccount) => {
    // Track activation event
    track("success account activated");

    // Send HubSpot events
    await handleLoginAnalyticsEvents(account);

    // Mark as first login if not already done
    if (!account.firstLogin) setFirstTimeLogin();

    // Navigate to wizard if no blocking conditions
    const expiredKey = "storydoc_workspace_expired"; // Assuming this exists
    const hasExpiredWorkspace = !!getCookie(expiredKey);

    if (!hasExpiredWorkspace) {
      navigate(FUNNEL_PATHS.root);
    }
  };

  /**
   * Main handler for first-time login logic
   */
  const handleFirstTimeLogin = async (
    account: TAccount,
    user: TUser,
    isAdminCore = false,
    claims: ParsedToken,
  ) => {
    const isFirstLogin = !account.firstLogin;
    const isSameAccount = account._id === user.orgId;
    const isCreatedAfterJune15 =
      new Date(account.createdAt).getTime() >
      new Date("2021-06-15T00:00:00.000+00:00").getTime();

    // Update user first login if not already done
    if (!user.firstLogin) updateUserFirstLogin();

    if (isFirstLogin && !isAdminCore && isCreatedAfterJune15) {
      handleFirstLoginFlow(account);
    }

    if (!isFirstLogin && isSameAccount) {
      checkWizardPayloadAndNavigate(navigate, claims);
    }
  };

  // Main first-time login processing
  useEffect(() => {
    if (accountLoading || userLoading || processed || !account || !user) {
      return;
    }

    (async () => {
      try {
        const claims = await getCustomClaims();
        const isAdminCore = claims?.admin === true;

        await handleFirstTimeLogin(account, user, isAdminCore, claims);
        setProcessed(true);
      } catch (error) {
        console.error("Error processing first-time login:", error);
        setProcessed(true);
      }
    })();
  }, [account, user, accountLoading, userLoading, processed]);
};
